import { css, html, LitElement, nothing, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { ReadingBeeModal } from "./component.modal.js";
import { gearIcon } from "./icons.js";
import { navigate, viewFromPath } from "./nav.js";
import { globalStyles } from "./styles.global.js";
import { appUpdate, AppUpdatePhase } from "./sw-update.js";
import "./component.modal.js";

@customElement("reading-bee-update-chip")
export class ReadingBeeUpdateChip extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        position: fixed;
        inset: 0;
        z-index: 80;
        pointer-events: none;
      }

      :host([hidden]) {
        display: none;
      }

      reading-bee-modal {
        pointer-events: auto;
      }

      .chip {
        pointer-events: auto;
        position: absolute;
        top: calc(0.7rem + var(--inset-top) + 4px);
        left: 1.15rem;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        min-height: 36px;
        padding: 0.3rem 0.75rem 0.3rem 0.5rem;
        border-radius: 999px;
        font-size: 0.82rem;
        font-weight: 800;
        letter-spacing: 0.01em;
        white-space: nowrap;
        animation: chipIn 280ms ease;
      }

      .chip.is-updating {
        background: var(--color-1, #e8b84a);
        color: #1a1408;
        border: 0;
      }

      .chip.is-updated {
        padding: 0.28rem 0.65rem;
        background: #1a1713;
        border: 1px solid rgba(125, 206, 130, 0.5);
        color: var(--color-success, #7dce82);
        font-size: 0.78rem;
        font-weight: 700;
      }

      .chip.is-updating .gear {
        display: grid;
        width: 1.05rem;
        height: 1.05rem;
        place-items: center;
        flex: 0 0 auto;
      }

      .chip.is-updating svg {
        width: 1.05rem;
        height: 1.05rem;
        animation: spin 1.1s linear infinite;
      }

      .chip.is-leaving {
        opacity: 0;
        transform: translateX(-6px);
        animation: none;
        transition:
          opacity 280ms ease,
          transform 280ms ease;
      }

      .meter {
        display: grid;
        gap: 0.7rem;
      }

      .row {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.8rem;
      }

      .label {
        margin: 0;
        color: var(--color-primary-text-muted);
      }

      .percent {
        margin: 0;
        font-variant-numeric: tabular-nums;
        font-weight: 800;
        color: var(--color-1);
      }

      .bar {
        height: 10px;
        border-radius: 999px;
        background: #221e18;
        overflow: hidden;
      }

      .fill {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: var(--color-1);
        transition: width 160ms ease;
      }

      .bar.is-wait .fill {
        width: 34% !important;
        animation: wait 1.1s ease-in-out infinite;
      }

      .settings-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        width: 100%;
        margin-top: 0.35rem;
        padding: 0;
        color: var(--color-primary-text-muted);
        font-size: 0.92rem;
        text-decoration: underline;
        text-underline-offset: 0.18em;
      }

      .settings-link:hover {
        color: var(--color-primary-text);
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes wait {
        0% {
          transform: translateX(-110%);
        }
        100% {
          transform: translateX(280%);
        }
      }

      @keyframes chipIn {
        from {
          opacity: 0;
          transform: translateX(-8px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .chip.is-updating svg,
        .chip,
        .bar.is-wait .fill {
          animation: none;
        }
      }
    `,
  ];

  @state() private phase: AppUpdatePhase = "idle";
  @state() private percent = 0;
  @state() private total = 0;
  @state() private onReading = viewFromPath() === "reading";
  @query("reading-bee-modal") private modal!: ReadingBeeModal;

  override connectedCallback(): void {
    super.connectedCallback();
    this.sync();
    this.onReading = viewFromPath() === "reading";
    appUpdate.addEventListener("change", this.onChange);
    window.addEventListener("popstate", this.onPopState);
  }

  override disconnectedCallback(): void {
    appUpdate.removeEventListener("change", this.onChange);
    window.removeEventListener("popstate", this.onPopState);
    super.disconnectedCallback();
  }

  override render(): TemplateResult {
    if (this.phase === "idle") {
      return html``;
    }
    const waiting = this.phase === "updating" && this.total <= 0;
    return html`
      ${
        this.phase === "updating"
          ? html`
              <button class="chip is-updating" aria-label="Updating app" @click=${this.openProgress}>
                <span class="gear" aria-hidden="true">${gearIcon}</span>
                Updating
              </button>
            `
          : html`
              <span
                class="chip is-updated ${this.phase === "leaving" ? "is-leaving" : ""}"
                role="status"
                aria-live="polite">
                App updated
              </span>
            `
      }
      <reading-bee-modal>
        <h2 slot="title">Updating app</h2>
        <div slot="body" class="meter">
          <div class="row">
            <p class="label">${waiting ? "Preparing the new version" : "Updating…"}</p>
            <p class="percent">${waiting ? "—" : `${this.percent}%`}</p>
          </div>
          <div
            class="bar ${waiting ? "is-wait" : ""}"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow=${waiting ? nothing : this.percent}
            aria-label="App update">
            <span class="fill" style="width:${waiting ? 34 : this.percent}%"></span>
          </div>
          <button class="settings-link" type="button" @click=${this.goLink}>
            ${this.onReading ? "Go to settings" : "Go to learning"}
          </button>
        </div>
      </reading-bee-modal>
    `;
  }

  protected override updated(): void {
    this.toggleAttribute("hidden", this.phase === "idle");
  }

  private onChange = (): void => {
    this.sync();
  };

  private sync = (): void => {
    this.phase = appUpdate.phase;
    this.percent = appUpdate.percent;
    this.total = appUpdate.total;
    this.toggleAttribute("hidden", this.phase === "idle");
  };

  private onPopState = (): void => {
    this.onReading = viewFromPath() === "reading";
  };

  private openProgress = (): void => {
    this.onReading = viewFromPath() === "reading";
    void this.modal.open();
  };

  private goLink = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    const dest = this.onReading ? "settings" : "reading";
    void this.modal?.close();
    navigate(dest);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-update-chip": ReadingBeeUpdateChip;
  }
}
